import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Button, TextField, Grid, Typography, Snackbar, Alert, MenuItem, Select,
    FormControl, Checkbox, Box, Dialog, DialogActions, DialogContent,
    DialogTitle, Paper, Stack, Switch, FormControlLabel, IconButton, ListItemText, List, ListItemButton,
    FormHelperText, CircularProgress, InputLabel, // Added InputLabel
    useTheme, alpha // Added alpha for styling
} from '@mui/material';
import { TimePicker, DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axiosInstance from '../../../api/axiosConfig'; // Assuming path is correct
import {
    AddCircleOutline as AddCircleIcon, // Use outlined
    RemoveCircleOutline as RemoveCircleIcon, // Use outlined
    Save as SaveIcon,
    Settings as SettingsIcon, // Section icon
    Event as EventIcon, // Section icon
    Schedule as ScheduleIcon, // Section icon
    NotificationsActive as NotificationsIcon, // Section icon
    WarningAmber as WarningIcon, // For dialogs
    Download as DownloadIcon, // For export button
    LockClock as LockClockIcon, // For close year button
} from '@mui/icons-material';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'; // For date validation if needed
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'; // For date validation if needed
import { ADMIN_LAYOUT } from '../../../adminThemeTokens';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const AcademicSettings = () => {
    const schoolId = localStorage.getItem('schoolId');
    const theme = useTheme();

    // State
    const [settings, setSettings] = useState(null); // Initial state null to indicate not loaded
    const [editedSettings, setEditedSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveConfirmDialogOpen, setSaveConfirmDialogOpen] = useState(false);
    const [closeYearDialogOpen, setCloseYearDialogOpen] = useState(false);
    const [isClosingYear, setIsClosingYear] = useState(false);
    const [isExporting, setIsExporting] = useState(false); // Renamed for clarity
    const [newAcademicYear, setNewAcademicYear] = useState('');
    const [closeYearConfirmationText, setCloseYearConfirmationText] = useState('');
    const [closeYearConfirmationError, setCloseYearConfirmationError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [validationErrors, setValidationErrors] = useState({});
    const [activeSection, setActiveSection] = useState('general');

    // --- Data Fetching and Parsing ---

    const parseSettings = useCallback((data) => {
        if (!data) return null;
        const parsed = { ...data };

        // Convert boolean strings to actual booleans
        ['sms_enabled', 'whatsapp_enabled', 'is_second_saturday_holiday', 'absent_alert_enabled', 'absent_alert_sms_enabled', 'absent_alert_whatsapp_enabled', 'absent_alert_in_app_enabled'].forEach((key) => {
            if (parsed[key] !== undefined && parsed[key] !== null) {
                parsed[key] = String(parsed[key]).toLowerCase() === 'true';
            } else {
                 parsed[key] = false; // Default to false if undefined/null
            }
        });

        parsed['absent_alert_message_template'] = parsed['absent_alert_message_template']
            || 'Dear Parent, {studentName} was marked absent on {date} for Class {className} {sectionName}. - {schoolName}';

        // Parse break times (string "HH:mm-HH:mm,HH:mm-HH:mm" to array of objects)
        parsed['break_times'] = (parsed['break_times'] || '')
            .split(',')
            .map((range) => range.trim())
            .filter(Boolean) // Remove empty strings
            .map((range) => {
                const [start, end] = range.split('-').map(t => t.trim());
                // Add basic validation for format if needed, here assuming format is correct
                return { start: start || '', end: end || '' };
            });
        if (!Array.isArray(parsed['break_times'])) {
            parsed['break_times'] = [];
        }

        // Parse school hours (string "HH:mm-HH:mm" to object)
        const [startHour, endHour] = (parsed['school_hours'] || '-').split('-').map(t => t.trim());
        parsed['school_hours'] = { start: startHour || '', end: endHour || '' };

        // Parse academic year start date
        parsed['academic_year_start'] = parsed['academic_year_start'] ? dayjs(parsed['academic_year_start']) : null;

        // Parse working days (string "Monday,Tuesday,..." to array)
        parsed['working_days'] = (parsed['working_days'] || '').split(',').map(d => d.trim()).filter(Boolean);
        if (!Array.isArray(parsed['working_days'])) {
            parsed['working_days'] = [];
        }

        // Subscription Plan - keep as string
        parsed['subscription_plans'] = parsed['subscription_plans'] || 'Basic'; // Default to Basic

        // Academic Years History - keep as string
        parsed['academic_years_history'] = parsed['academic_years_history'] || '';

        return parsed;
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/api/settings/school/${schoolId}`);
            const settingsData = parseSettings(response.data);
            setSettings(settingsData);
            setEditedSettings(settingsData); // Initialize edited settings
            setSnackbar({ open: false }); // Close any previous snackbar
        } catch (error) {
            console.error('Error fetching settings:', error);
            setSnackbar({ open: true, message: 'Error fetching settings. Please try again.', severity: 'error' });
            setSettings({}); // Set to empty object on error to avoid render issues
            setEditedSettings({});
        } finally {
            setLoading(false);
        }
    }, [schoolId, parseSettings]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- State Update Handlers ---

    const handleFieldChange = useCallback((name, value) => {
        setEditedSettings((prev) => ({ ...prev, [name]: value }));
        // Clear validation error for this field on change
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: null }));
        }
    }, [validationErrors]);

    const handleToggleChange = useCallback((name, checked) => {
        handleFieldChange(name, checked);
    }, [handleFieldChange]);

    const handleWorkingDaysChange = useCallback((event) => {
        const { value } = event.target;
        // Ensure value is always an array and remove duplicates
        const uniqueWorkingDays = Array.from(new Set(typeof value === 'string' ? value.split(',') : value));
        handleFieldChange('working_days', uniqueWorkingDays);
    }, [handleFieldChange]);

    const handleTimeChange = useCallback((name, type, time) => {
        const formattedTime = time ? time.format('HH:mm') : '';
        setEditedSettings((prev) => ({
            ...prev,
            [name]: {
                ...prev[name],
                [type]: formattedTime,
            },
        }));
         // Clear validation error for this composite field on change
         if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: null }));
        }
    }, [validationErrors]);

     const handleBreakTimeChange = useCallback((index, type, time) => {
        const formattedTime = time ? time.format('HH:mm') : '';
        setEditedSettings((prev) => {
            const updatedBreakTimes = [...prev.break_times];
            updatedBreakTimes[index] = { ...updatedBreakTimes[index], [type]: formattedTime };
            return { ...prev, break_times: updatedBreakTimes };
        });
         // Optionally clear validation errors related to breaks if needed
    }, []);


    const addBreakTime = useCallback(() => {
        setEditedSettings((prev) => ({
            ...prev,
            break_times: [...prev.break_times, { start: '', end: '' }],
        }));
    }, []);

    const removeBreakTime = useCallback((index) => {
        setEditedSettings((prev) => {
            const updatedBreakTimes = [...prev.break_times];
            updatedBreakTimes.splice(index, 1);
            return { ...prev, break_times: updatedBreakTimes };
        });
    }, []);

    const handleDateChange = useCallback((name, date) => {
        handleFieldChange(name, date);
    }, [handleFieldChange]);


    // --- Validation ---
    const validateSettings = useCallback(() => {
        const errors = {};
        const { school_hours, break_times, academic_year_start } = editedSettings;
        const safeBreakTimes = Array.isArray(break_times) ? break_times : [];

        // School Hours validation
        if (!school_hours?.start || !school_hours?.end) {
            errors.school_hours = 'School start and end times are required.';
        } else {
            const start = dayjs(`1970-01-01T${school_hours.start}`);
            const end = dayjs(`1970-01-01T${school_hours.end}`);
            if (start.isValid() && end.isValid() && start.isSameOrAfter(end)) {
                errors.school_hours = 'School start time must be before end time.';
            }
        }

        // Break Times validation
        safeBreakTimes.forEach((bt, index) => {
             if (!bt.start || !bt.end) {
                errors[`break_time_${index}`] = `Break ${index + 1} start and end times are required.`;
             } else {
                 const breakStart = dayjs(`1970-01-01T${bt.start}`);
                 const breakEnd = dayjs(`1970-01-01T${bt.end}`);
                 if (breakStart.isValid() && breakEnd.isValid() && breakStart.isSameOrAfter(breakEnd)) {
                     errors[`break_time_${index}`] = `Break ${index + 1} start time must be before end time.`;
                 }
             }
        });

        // Academic Year Start (Optional: Add validation if needed, e.g., must be a valid date)
        if (academic_year_start && !dayjs(academic_year_start).isValid()) {
             errors.academic_year_start = 'Invalid date format for Academic Year Start.';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0; // Return true if no errors
    }, [editedSettings]);


    // --- Data Preparation for Saving ---
    const prepareSettingsForSave = useCallback(() => {
        const settingsToSave = {};
        for (const key in editedSettings) {
            if (Object.hasOwnProperty.call(editedSettings, key)) {
                let value = editedSettings[key];

                if (typeof value === 'boolean') {
                    settingsToSave[key] = String(value); // Convert boolean to 'true'/'false' string for API
                } else if (key === 'break_times' && Array.isArray(value)) {
                    settingsToSave[key] = value.map(bt => `${bt.start || ''}-${bt.end || ''}`).join(',');
                } else if (key === 'school_hours' && typeof value === 'object' && value !== null) {
                    settingsToSave[key] = `${value.start || ''}-${value.end || ''}`;
                } else if (key === 'working_days' && Array.isArray(value)) {
                    settingsToSave[key] = value.join(',');
                } else if (key === 'academic_year_start' && dayjs.isDayjs(value)) {
                    settingsToSave[key] = value.isValid() ? value.format('YYYY-MM-DD') : null;
                } else if (value === null || value === undefined) {
                     settingsToSave[key] = null; // Send null for empty/undefined values if needed by backend
                }
                 else {
                    settingsToSave[key] = value; // Keep other values (like strings) as they are
                }
            }
        }
         // Remove fields the backend might not expect if they came from parsing only
         // delete settingsToSave.academic_years_history; // Example if this is read-only

        return settingsToSave;
    }, [editedSettings]);


    // --- Save Action ---
    const handleSave = async () => {
        if (!validateSettings()) {
             setSnackbar({ open: true, message: 'Please fix the validation errors.', severity: 'warning' });
             setSaveConfirmDialogOpen(false);
             return;
        }

        setIsSaving(true);
        setSaveConfirmDialogOpen(false);
        const payload = prepareSettingsForSave();

        try {
            await axiosInstance.put(`/api/schools/${schoolId}/settings`, payload);
            setSettings(editedSettings); // Update the 'source of truth' state
            setSnackbar({ open: true, message: 'Settings saved successfully.', severity: 'success' });
        } catch (err) {
            console.error('Error saving settings:', err);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Error saving settings.', severity: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    // --- Academic Year Actions ---
    const handleOpenCloseYearDialog = () => {
        setCloseYearConfirmationText('');
        setCloseYearConfirmationError('');
        setCloseYearDialogOpen(true);
    };

    const handleConfirmCloseAcademicYear = async () => {
        if (closeYearConfirmationText !== "I want to close academic year") {
            setCloseYearConfirmationError("Confirmation text does not match.");
            return;
        }
         const yearRegex = /^\d{4}-\d{4}$/; // Simple regex for YYYY-YYYY format
         if (!newAcademicYear || !yearRegex.test(newAcademicYear)) {
             setSnackbar({ open: true, message: 'Please enter the New Academic Year in YYYY-YYYY format (e.g., 2024-2025).', severity: 'warning' });
             setCloseYearConfirmationError("Invalid format for New Academic Year."); // Also show error in dialog
             return;
         }

        setIsClosingYear(true);
        setCloseYearConfirmationError(''); // Clear error on proceed
        setCloseYearDialogOpen(false);

        try {
            // IMPORTANT: The API should handle setting the *new* academic year based on the parameter
            // AND updating the *history* internally. The frontend shouldn't manually update history.
            await axiosInstance.put(`/api/schools/${schoolId}/academic-year/close?newAcademicYear=${encodeURIComponent(newAcademicYear)}`);
            setSnackbar({ open: true, message: 'Academic year closed successfully. New year set.', severity: 'success' });
            setNewAcademicYear(''); // Clear the input field
            await fetchData(); // Re-fetch settings to get updated current year and history from backend
        } catch (err) {
            console.error('Error closing academic year:', err);
             setSnackbar({ open: true, message: err.response?.data?.message || 'Error closing academic year.', severity: 'error' });
        } finally {
            setIsClosingYear(false);
        }
    };

    const handleExportAcademicYear = async () => {
        setIsExporting(true);
         setSnackbar({ open: false }); // Close previous snackbars

        try {
            // **Replace with actual API call**
            // const response = await axiosInstance.get(`/api/schools/${schoolId}/academic-year/export`, { responseType: 'blob' });
            // // Trigger file download
            // const url = window.URL.createObjectURL(new Blob([response.data]));
            // const link = document.createElement('a');
            // link.href = url;
            // link.setAttribute('download', `academic_year_${settings?.academic_year || 'data'}.xlsx`); // Or appropriate extension
            // document.body.appendChild(link);
            // link.click();
            // link.parentNode.removeChild(link);
            // window.URL.revokeObjectURL(url);

            // Simulate API call delay
             await new Promise(resolve => setTimeout(resolve, 1500));
             setSnackbar({ open: true, message: 'Academic year data export initiated (Simulated).', severity: 'info' });

        } catch (err) {
            console.error('Error exporting academic year data:', err);
            setSnackbar({ open: true, message: 'Error exporting academic year data (Simulated).', severity: 'error' });
        } finally {
            setIsExporting(false);
        }
    };


    // --- Helper Functions ---
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    // Memoize checking if settings have changed to disable save button
    const hasChanges = useMemo(() => {
        return JSON.stringify(settings) !== JSON.stringify(editedSettings);
    }, [settings, editedSettings]);

    const sectionItems = useMemo(() => ([
        {
            id: 'general',
            title: 'General',
            description: 'Academic year start and subscription plan.',
            icon: <SettingsIcon fontSize="small" color="primary" />,
        },
        {
            id: 'year',
            title: 'Academic Year',
            description: 'Current year status, export, and close-year control.',
            icon: <EventIcon fontSize="small" color="primary" />,
        },
        {
            id: 'schedule',
            title: 'Time & Schedule',
            description: 'School hours, working days, breaks, and holiday rules.',
            icon: <ScheduleIcon fontSize="small" color="primary" />,
        },
        {
            id: 'communication',
            title: 'Communication',
            description: 'SMS, WhatsApp, and absent alert automation.',
            icon: <NotificationsIcon fontSize="small" color="primary" />,
        },
    ]), []);

    const currentSection = sectionItems.find((item) => item.id === activeSection) || sectionItems[0];


    // --- Render Logic ---
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!editedSettings) {
         // Handle case where initial fetch failed but not loading anymore
         return (
             <Box sx={{ p: 3 }}>
                 <Alert severity="error">Could not load academic settings. Please try refreshing the page.</Alert>
             </Box>
         );
    }


    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: ADMIN_LAYOUT.pagePadding, maxWidth: '100%' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2.5 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            Academic Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Use the vertical list to open one academic configuration area at a time.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() => setSaveConfirmDialogOpen(true)}
                        disabled={isSaving || isClosingYear || isExporting || !hasChanges}
                        startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    >
                        Save Changes
                    </Button>
                </Stack>

                <Grid container spacing={2.5} alignItems="flex-start">
                    <Grid item xs={12} lg={3.4}>
                        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 3 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', px: 1, pb: 1, fontWeight: 700 }}>
                                Academic Sections
                            </Typography>
                            <List disablePadding>
                                {sectionItems.map((item) => (
                                    <ListItemButton
                                        key={item.id}
                                        selected={activeSection === item.id}
                                        onClick={() => setActiveSection(item.id)}
                                        sx={{
                                            mb: 1,
                                            border: '1px solid',
                                            borderColor: activeSection === item.id ? 'primary.main' : 'divider',
                                            borderRadius: 1.5,
                                            alignItems: 'flex-start',
                                            py: 1.5,
                                            '&.Mui-selected': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                            },
                                            '&.Mui-selected:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                            },
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.2} sx={{ width: '100%' }}>
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: 2,
                                                    display: 'grid',
                                                    placeItems: 'center',
                                                    bgcolor: alpha(theme.palette.primary.main, activeSection === item.id ? 0.14 : 0.06),
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {item.icon}
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    {item.title}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
                                                    {item.description}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </ListItemButton>
                                ))}
                            </List>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} lg={8.6}>
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2.5 }}>
                                <Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {currentSection.icon}
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {currentSection.title}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                                        {currentSection.description}
                                    </Typography>
                                </Box>
                            </Stack>

                    {activeSection === 'general' && (
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} md={6}>
                                <DatePicker
                                    label="Academic Year Start Date"
                                    value={editedSettings.academic_year_start || null}
                                    onChange={(date) => handleDateChange('academic_year_start', date)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small', helperText: "When the current academic session begins." } }}

                                />
                                {validationErrors.academic_year_start && <FormHelperText error>{validationErrors.academic_year_start}</FormHelperText>}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="subscription-plan-label">Subscription Plan</InputLabel>
                                    <Select
                                        labelId="subscription-plan-label"
                                        label="Subscription Plan"
                                        name="subscription_plans"
                                        value={editedSettings.subscription_plans || ''}
                                        onChange={(e) => handleFieldChange('subscription_plans', e.target.value)}
                                    >
                                        {['Basic', 'Premium', 'Enterprise'].map((plan) => (
                                            <MenuItem key={plan} value={plan}>{plan}</MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>Select the active plan for your school.</FormHelperText>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}

                    {activeSection === 'year' && (
                        <Grid container spacing={2.5} alignItems="center">
                            <Grid item xs={12} md={6}>
                                 <Typography variant="body2" color="textSecondary" gutterBottom>
                                     Current Academic Year: <strong>{settings?.academic_year || 'Not Set'}</strong>
                                 </Typography>
                                {settings?.academic_years_history && (
                                    <Typography variant="body2" color="textSecondary">
                                        Past Years: <Typography component="span" variant="caption">{settings.academic_years_history}</Typography>
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Next Academic Year (YYYY-YYYY)"
                                    placeholder="e.g., 2024-2025"
                                    variant="outlined"
                                    size="small"
                                    value={newAcademicYear}
                                    onChange={(e) => setNewAcademicYear(e.target.value)}
                                    helperText="Enter the upcoming academic year before closing the current one."
                                />
                            </Grid>
                             <Grid item xs={12} container spacing={1.5} justifyContent="flex-end">
                                 <Grid item>
                                     <Button
                                         variant="outlined"
                                         onClick={handleExportAcademicYear}
                                         disabled={isClosingYear || isExporting}
                                         startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                                     >
                                         Export Data
                                     </Button>
                                </Grid>
                                 <Grid item>
                                     <Button
                                         variant="contained"
                                         color="secondary"
                                         onClick={handleOpenCloseYearDialog}
                                         disabled={isClosingYear || isExporting || !newAcademicYear} // Disable if no new year entered
                                         startIcon={isClosingYear ? <CircularProgress size={20} /> : <LockClockIcon />}
                                     >
                                         Close Current Year
                                     </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}


                    {activeSection === 'schedule' && (
                        <Grid container spacing={3}>
                             {/* School Hours */}
                             <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>School Hours</Typography>
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                     <TimePicker
                                         label="Start Time"
                                         value={editedSettings.school_hours?.start ? dayjs(`1970-01-01T${editedSettings.school_hours.start}`) : null}
                                         onChange={(time) => handleTimeChange('school_hours', 'start', time)}
                                         slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                     />
                                     <TimePicker
                                         label="End Time"
                                         value={editedSettings.school_hours?.end ? dayjs(`1970-01-01T${editedSettings.school_hours.end}`) : null}
                                         onChange={(time) => handleTimeChange('school_hours', 'end', time)}
                                          slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                     />
                                </Stack>
                                 {validationErrors.school_hours && <FormHelperText error sx={{ mt: 1 }}>{validationErrors.school_hours}</FormHelperText>}
                            </Grid>

                            {/* Working Days */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="working-days-label">Working Days</InputLabel>
                                    <Select
                                        labelId="working-days-label"
                                        label="Working Days"
                                        multiple
                    value={editedSettings.working_days || []}
                                        onChange={handleWorkingDaysChange}
                                        renderValue={(selected) => selected.join(', ')}
                                        MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                                    >
                                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                                            <MenuItem key={day} value={day}>
                                                <Checkbox checked={(editedSettings.working_days || []).includes(day)} size="small" />
                                                <ListItemText primary={day} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>Select the standard working days.</FormHelperText>
                                </FormControl>
                            </Grid>

                             {/* Break Times */}
                             <Grid item xs={12}>
                                 <Typography variant="subtitle2" sx={{ mb: 1 }}>Break Times</Typography>
                                 <Stack spacing={1.5}>
                                     {(Array.isArray(editedSettings.break_times) ? editedSettings.break_times : []).map((bt, index) => (
                                         <Stack key={index} direction="row" spacing={2} alignItems="center">
                                             <TimePicker
                                                 label={`Break ${index + 1} Start`}
                                                 value={bt.start ? dayjs(`1970-01-01T${bt.start}`) : null}
                                                 onChange={(time) => handleBreakTimeChange(index, 'start', time)}
                                                 slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                             />
                                             <TimePicker
                                                 label={`Break ${index + 1} End`}
                                                 value={bt.end ? dayjs(`1970-01-01T${bt.end}`) : null}
                                                 onChange={(time) => handleBreakTimeChange(index, 'end', time)}
                                                 slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                             />
                                             <IconButton
                                                 aria-label="Remove break time"
                                                 onClick={() => removeBreakTime(index)}
                                                 size="small"
                                                 color="error"
                                                 sx={{mt: '2px'}} // Align better with textfields
                                             >
                                                 <RemoveCircleIcon />
                                             </IconButton>
                                             {validationErrors[`break_time_${index}`] && <FormHelperText error>{validationErrors[`break_time_${index}`]}</FormHelperText>}
                                         </Stack>
                                     ))}
                                 </Stack>
                                 <Button
                                     variant="outlined"
                                     startIcon={<AddCircleIcon />}
                                     onClick={addBreakTime}
                                     size="small"
                                     sx={{ mt: 1.5 }}
                                 >
                                     Add Break
                                 </Button>
                            </Grid>

                            {/* Second Saturday Holiday */}
                             <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editedSettings.is_second_saturday_holiday || false}
                                            onChange={(e) => handleToggleChange('is_second_saturday_holiday', e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Second Saturday is Holiday"
                                />
                                 <FormHelperText>If enabled, the second Saturday of each month will be marked as a holiday automatically.</FormHelperText>
                            </Grid>

                        </Grid>
                    )}

                    {activeSection === 'communication' && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editedSettings.sms_enabled || false}
                                            onChange={(e) => handleToggleChange('sms_enabled', e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Enable SMS Notifications"
                                />
                                <FormHelperText>Allow sending notifications via SMS (requires configuration and credits).</FormHelperText>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editedSettings.whatsapp_enabled || false}
                                            onChange={(e) => handleToggleChange('whatsapp_enabled', e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Enable WhatsApp Notifications"
                                />
                                <FormHelperText>Allow sending notifications via WhatsApp (requires configuration).</FormHelperText>
                            </Grid>
                            <Grid item xs={12}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        background: (theme) => alpha(theme.palette.primary.main, 0.035),
                                    }}
                                >
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                Auto absent alert
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                                                Automatically notify when a student is marked absent. This works from attendance submission and manual absence updates.
                                            </Typography>
                                        </Box>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={editedSettings.absent_alert_enabled || false}
                                                            onChange={(e) => handleToggleChange('absent_alert_enabled', e.target.checked)}
                                                            size="small"
                                                        />
                                                    }
                                                    label="Enable absent alert automation"
                                                />
                                                <FormHelperText>Turn on the automatic trigger for absent students.</FormHelperText>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.8 }}>
                                                    Delivery channels
                                                </Typography>
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={editedSettings.absent_alert_sms_enabled || false}
                                                                onChange={(e) => handleToggleChange('absent_alert_sms_enabled', e.target.checked)}
                                                                size="small"
                                                                disabled={!editedSettings.absent_alert_enabled}
                                                            />
                                                        }
                                                        label="SMS"
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={editedSettings.absent_alert_whatsapp_enabled || false}
                                                                onChange={(e) => handleToggleChange('absent_alert_whatsapp_enabled', e.target.checked)}
                                                                size="small"
                                                                disabled={!editedSettings.absent_alert_enabled}
                                                            />
                                                        }
                                                        label="WhatsApp"
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={editedSettings.absent_alert_in_app_enabled || false}
                                                                onChange={(e) => handleToggleChange('absent_alert_in_app_enabled', e.target.checked)}
                                                                size="small"
                                                                disabled={!editedSettings.absent_alert_enabled}
                                                            />
                                                        }
                                                        label="In-app"
                                                    />
                                                </Stack>
                                                <FormHelperText>SMS and WhatsApp respect the school-wide communication switches above.</FormHelperText>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Absent alert message template"
                                                    fullWidth
                                                    multiline
                                                    minRows={3}
                                                    value={editedSettings.absent_alert_message_template || ''}
                                                    onChange={(e) => handleFieldChange('absent_alert_message_template', e.target.value)}
                                                    placeholder="Dear Parent, {studentName} was marked absent on {date} for Class {className} {sectionName}. - {schoolName}"
                                                    helperText="Supported placeholders: {studentName}, {date}, {className}, {sectionName}, {schoolName}, {admissionNumber}."
                                                    disabled={!editedSettings.absent_alert_enabled}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                        </Paper>
                    </Grid>
                </Grid>

                {/* --- Dialogs --- */}

                {/* Save Confirmation Dialog */}
                <Dialog open={saveConfirmDialogOpen} onClose={() => setSaveConfirmDialogOpen(false)} maxWidth="xs">
                    <DialogTitle sx={{ fontSize: '1.1rem' }}>Confirm Save</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">
                            Save the updated academic settings?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={() => setSaveConfirmDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} color="primary" variant="contained" disabled={isSaving}>
                            Confirm Save
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Close Academic Year Confirmation Dialog */}
                <Dialog open={closeYearDialogOpen} onClose={() => setCloseYearDialogOpen(false)} maxWidth="sm">
                    <DialogTitle sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                         <WarningIcon color="warning"/> Confirm Academic Year Closure
                    </DialogTitle>
                    <DialogContent sx={{ pt: 1.5 }}>
                        <Typography variant="h6" gutterBottom>
                            <b>Important Considerations:</b>
                        </Typography>
                        <Stack spacing={1.5}>
                            <Typography variant="body2">
                                1.  <b>Export Data First:</b> It's highly recommended to use the <strong>"Export Data"</strong> button to back up the current academic year's information <i>before</i> proceeding.
                            </Typography>
                             <Typography variant="body2">
                                2.  <b>New Academic Year:</b> Ensure you have correctly entered the <strong>Next Academic Year</strong> (e.g., {dayjs().year()}-{dayjs().year() + 1}) in the field above. This will become the active year.
                            </Typography>
                            <Typography variant="body2">
                                3.  <b>Irreversible Action:</b> Closing the year archives the current data. While backups exist, switching back requires admin support (<a href="mailto:support@skhool.in">support@skhool.in</a>).
                            </Typography>
                        </Stack>
                        <TextField
                            fullWidth
                            label="Type 'I want to close academic year' to confirm"
                            value={closeYearConfirmationText}
                            onChange={(e) => setCloseYearConfirmationText(e.target.value)}
                            margin="normal"
                            size="small"
                            error={!!closeYearConfirmationError}
                            helperText={closeYearConfirmationError || "This action cannot be undone easily."}
                            autoFocus
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={() => setCloseYearDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={handleConfirmCloseAcademicYear}
                            color="secondary"
                            variant="contained"
                            disabled={isClosingYear || !newAcademicYear || closeYearConfirmationText !== "I want to close academic year"}
                            startIcon={isClosingYear ? <CircularProgress size={20}/> : null}
                        >
                            Confirm & Close Year
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* --- Snackbar for Feedback --- */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
};

export default AcademicSettings;
